import { db } from '../firebase';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  setDoc,
  query, 
  where, 
  getDocs, 
  serverTimestamp,
  getDoc 
} from 'firebase/firestore';

/**
 * Analytics Service for tracking user interactions and sessions
 * Handles event tracking, session management, and data attribution
 */
export class AnalyticsService {
  
  // Session timeout in milliseconds (1 hour)
  static SESSION_TIMEOUT = 60 * 60 * 1000;
  
  /**
   * Create a new user session
   * @param {string} userId - User ID
   * @param {Array<string>} organizationIds - Array of organization IDs user belongs to
   * @returns {Promise<string>} Session ID
   */
  static async createSession(userId, organizationIds = []) {
    // Prepare session data outside try block so it can be reused
    const sessionData = {
      userId,
      organizationIds,
      loginTime: serverTimestamp(),
      lastActivity: serverTimestamp(),
      logoutTime: null,
      duration: null,
      isActive: true,
      createdAt: serverTimestamp()
    };

    try {
      // Generate a unique session ID using timestamp and random component
      const sessionId = `${userId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Use setDoc with merge to handle potential duplicates gracefully
      const sessionRef = doc(db, 'user_sessions', sessionId);
      await setDoc(sessionRef, sessionData, { merge: true });
      // Session created successfully
      return sessionId;
    } catch (error) {
      console.error('Error creating session:', error);
      // If it's a "document already exists" error, try to create with a new ID
      if (error.code === 'already-exists') {
        // Retrying with new session ID
        // Generate a new ID with additional randomness
        const retrySessionId = `${userId}_${Date.now()}_${Math.random().toString(36).substr(2, 12)}_retry`;
        const sessionRef = doc(db, 'user_sessions', retrySessionId);
        await setDoc(sessionRef, sessionData, { merge: true });
        // Session created on retry
        return retrySessionId;
      }
      throw error;
    }
  }
  
  /**
   * Update session activity timestamp
   * @param {string} sessionId - Session ID
   */
  static async updateSessionActivity(sessionId) {
    if (!sessionId) return;
    
    try {
      await updateDoc(doc(db, 'user_sessions', sessionId), {
        lastActivity: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating session activity:', error);
      // Don't throw the error - we don't want Firestore issues to break the app
      // The activity tracking will continue to work for timer resets
    }
  }
  
  /**
   * End a user session
   * @param {string} sessionId - Session ID
   * @param {boolean} isAutoLogout - Whether this was an automatic logout
   */
  static async endSession(sessionId, isAutoLogout = false) {
    if (!sessionId) return;
    
    try {
      // Get session data to calculate duration
      const sessionDoc = await getDoc(doc(db, 'user_sessions', sessionId));
      if (!sessionDoc.exists()) return;
      
      const sessionData = sessionDoc.data();
      const loginTime = sessionData.loginTime?.toDate();
      const duration = loginTime ? Math.floor((Date.now() - loginTime.getTime()) / 1000) : null;
      
      await updateDoc(doc(db, 'user_sessions', sessionId), {
        logoutTime: serverTimestamp(),
        duration,
        isActive: false,
        autoLogout: isAutoLogout
      });
      

    } catch (error) {
      console.error('Error ending session:', error);
    }
  }
  
  /**
   * Track a generic analytics event
   * @param {string} eventType - Type of event (login, logout, lesson_accessed, etc.)
   * @param {Object} resourceData - Resource information
   * @param {string} sessionId - Current session ID
   * @param {Array<string>} organizationIds - Organizations to attribute to
   */
  static async trackEvent(eventType, resourceData, sessionId, organizationIds = []) {
    try {
      // Filter out undefined values from metadata to prevent Firestore errors
      const cleanMetadata = {};
      if (resourceData.metadata) {
        Object.entries(resourceData.metadata).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            cleanMetadata[key] = value;
          }
        });
      }

      const eventData = {
        userId: resourceData.userId,
        organizationIds,
        eventType,
        resourceType: resourceData.resourceType || null,
        resourceId: resourceData.resourceId || null,
        courseId: resourceData.courseId || null,
        levelId: resourceData.levelId || null,
        sessionId,
        timestamp: serverTimestamp(),
        metadata: cleanMetadata
      };
      
      await addDoc(collection(db, 'analytics_events'), eventData);
    } catch (error) {
      console.error('Error tracking event:', error);
    }
  }
  
  /**
   * Track user login event
   * @param {string} userId - User ID
   * @param {string} sessionId - Session ID
   * @param {Array<string>} organizationIds - User's organizations
   */
  static async trackLogin(userId, sessionId, organizationIds = []) {
    await this.trackEvent('login', { userId }, sessionId, organizationIds);
  }
  
  /**
   * Track user logout event
   * @param {string} userId - User ID
   * @param {string} sessionId - Session ID
   * @param {Array<string>} organizationIds - User's organizations
   * @param {boolean} isAutoLogout - Whether this was an automatic logout
   */
  static async trackLogout(userId, sessionId, organizationIds = [], isAutoLogout = false) {
    await this.trackEvent('logout', { 
      userId,
      metadata: { isAutoLogout }
    }, sessionId, organizationIds);
  }
  

  
  /**
   * Check if a resource was already accessed in the current session
   * @param {string} sessionId - Session ID
   * @param {string} resourceId - Resource ID
   * @param {string} eventType - Event type to check for
   * @returns {Promise<boolean>} Whether resource was already accessed
   */
  static async isAlreadyAccessedInSession(sessionId, resourceId, eventType, userId) {
    if (!sessionId || !resourceId || !eventType || !userId) {
      return false;
    }

    try {
      const q = query(
        collection(db, 'analytics_events'),
        where('userId', '==', userId),
        where('sessionId', '==', sessionId),
        where('resourceId', '==', resourceId),
        where('eventType', '==', eventType)
      );

      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error checking session access:', error);
      return false;
    }
  }
  
  /**
   * Generate a unique session ID
   * @returns {string} Unique session identifier
   */
  static generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  
  /**
   * Convert Firestore Timestamp or Date to milliseconds for comparison.
   * @param {Object|Date|null|undefined} timestamp
   * @returns {number|null}
   */
  static getTimestampMs(timestamp) {
    if (!timestamp) return null;
    if (typeof timestamp.toDate === 'function') {
      return timestamp.toDate().getTime();
    }
    if (timestamp instanceof Date) {
      return timestamp.getTime();
    }
    return null;
  }

  /**
   * Whether an analytics session is still within the inactivity window.
   * @param {Object|null} session - Session document with lastActivity
   * @param {number} maxInactiveMs - Max idle time before session is stale
   * @returns {boolean}
   */
  static isSessionFresh(session, maxInactiveMs = AnalyticsService.SESSION_TIMEOUT) {
    if (!session) return false;

    const lastActivityMs =
      AnalyticsService.getTimestampMs(session.lastActivity) ??
      AnalyticsService.getTimestampMs(session.loginTime);

    if (lastActivityMs == null) return false;
    return Date.now() - lastActivityMs <= maxInactiveMs;
  }

  /**
   * Sort session docs by lastActivity descending (newest first).
   * @param {Array<Object>} sessions
   * @returns {Array<Object>}
   */
  static sortSessionsByLastActivity(sessions) {
    return [...sessions].sort((a, b) => {
      const aMs =
        AnalyticsService.getTimestampMs(a.lastActivity) ??
        AnalyticsService.getTimestampMs(a.loginTime) ??
        0;
      const bMs =
        AnalyticsService.getTimestampMs(b.lastActivity) ??
        AnalyticsService.getTimestampMs(b.loginTime) ??
        0;
      return bMs - aMs;
    });
  }

  /**
   * Fetch all active sessions for a user, optionally using a Firestore index.
   * @param {string} userId
   * @returns {Promise<Array<Object>>}
   */
  static async getActiveSessions(userId) {
    try {
      const fallbackQuery = query(
        collection(db, 'user_sessions'),
        where('userId', '==', userId),
        where('isActive', '==', true)
      );
      const fallbackSnapshot = await getDocs(fallbackQuery);
      return AnalyticsService.sortSessionsByLastActivity(
        fallbackSnapshot.docs.map((sessionDoc) => ({
          id: sessionDoc.id,
          ...sessionDoc.data()
        }))
      );
    } catch (error) {
      console.error('Error getting active sessions:', error);
      return [];
    }
  }

  /**
   * Get the most recently active session for a user.
   * @param {string} userId - User ID
   * @returns {Promise<Object|null>} Active session or null
   */
  static async getActiveSession(userId) {
    const sessions = await AnalyticsService.getActiveSessions(userId);
    return sessions.length > 0 ? sessions[0] : null;
  }

  /**
   * Close active sessions that exceeded the inactivity window.
   * @param {string} userId
   * @param {number} maxInactiveMs
   * @returns {Promise<number>} Number of sessions closed
   */
  static async closeStaleActiveSessions(userId, maxInactiveMs = AnalyticsService.SESSION_TIMEOUT) {
    const activeSessions = await AnalyticsService.getActiveSessions(userId);
    const staleSessions = activeSessions.filter(
      (session) => !AnalyticsService.isSessionFresh(session, maxInactiveMs)
    );

    if (staleSessions.length === 0) {
      return 0;
    }

    const results = await Promise.allSettled(
      staleSessions.map((session) => AnalyticsService.endSession(session.id, false))
    );

    const failures = results.filter((result) => result.status === 'rejected');
    if (failures.length > 0) {
      console.error(`Failed to close ${failures.length} stale session(s) for user ${userId}`);
    }

    return staleSessions.length;
  }

  /**
   * Resume a fresh session or create a new analytics session on login/visit.
   * @param {string} userId
   * @param {Array<string>} organizationIds
   * @returns {Promise<string|null>} Session ID
   */
  static async initializeSession(userId, organizationIds = []) {
    await AnalyticsService.closeStaleActiveSessions(userId);

    const existingSession = await AnalyticsService.getActiveSession(userId);
    if (existingSession && AnalyticsService.isSessionFresh(existingSession)) {
      return existingSession.id;
    }

    if (existingSession) {
      await AnalyticsService.endSession(existingSession.id, false);
    }

    const sessionId = await AnalyticsService.createSession(userId, organizationIds);
    await AnalyticsService.trackLogin(userId, sessionId, organizationIds);
    return sessionId;
  }

  /**
   * Track when a user accesses a resource (simple approach - no duplicate checking)
   * @param {string} resourceType - Type of resource ("lesson", "course", "prompt", "level")
   * @param {string} resourceId - ID of the resource
   * @param {string} sessionId - Current session ID
   * @param {string} userId - User ID
   * @param {Array<string>} organizationIds - Organization IDs
   * @param {Object} metadata - Additional context data
   */
  static async trackAccess(resourceType, resourceId, sessionId, userId, organizationIds, metadata = {}) {
    try {
      // Filter out undefined values from metadata to prevent Firestore errors
      const cleanMetadata = {};
      Object.entries(metadata).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          cleanMetadata[key] = value;
        }
      });

      // Track the access event (always create new document)
      const eventDoc = await addDoc(collection(db, 'analytics_events'), {
        eventType: `${resourceType}_accessed`,
        userId,
        sessionId,
        organizationIds,
        resourceId,
        resourceType,
        metadata: cleanMetadata,
        timestamp: serverTimestamp()
      });

      return eventDoc.id;
    } catch (error) {
      console.error(`Error tracking ${resourceType} access:`, error);
      return null;
    }
  }

  /**
   * Track when a user completes a resource
   * @param {string} resourceType - Type of resource ("lesson", "course", "prompt", "level")
   * @param {string} resourceId - ID of the resource
   * @param {string} sessionId - Current session ID
   * @param {string} userId - User ID
   * @param {Array<string>} organizationIds - Organization IDs
   * @param {Object} metadata - Additional context data
   */
  static async trackCompletion(resourceType, resourceId, sessionId, userId, organizationIds, metadata = {}) {
    try {
      // Filter out undefined values from metadata to prevent Firestore errors
      const cleanMetadata = {};
      Object.entries(metadata).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          cleanMetadata[key] = value;
        }
      });

      const eventDoc = await addDoc(collection(db, 'analytics_events'), {
        eventType: `${resourceType}_completed`,
        userId,
        sessionId,
        organizationIds,
        resourceId,
        resourceType,
        metadata: cleanMetadata,
        timestamp: serverTimestamp()
      });

      return eventDoc.id;
    } catch (error) {
      console.error(`Error tracking ${resourceType} completion:`, error);
      return null;
    }
  }



}

export default AnalyticsService;
